/**
 * Componente de conteúdo para mapeamento de referências externas
 * Exibe lista de referências por tipo com auto-match e seleção manual
 * 
 * RELEASE: 1.0.0 - 2026-02-03 - Fase 4: Wizard de Mapeamento
 * RELEASE: 2.0.0 - 2026-02-03 - Refatoração para componente de conteúdo (navegação no dialog pai)
 * RELEASE: 2.1.0 - 2026-02-03 - Suporte a criação automática de variáveis
 * RELEASE: 2.2.0 - 2026-03-22 - Criação automática expandida para tags de contato e negócio
 */

import { useMemo } from 'react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  AlertTriangle,
  Sparkles,
  User,
  Building2,
  Inbox,
  Tag,
  Briefcase,
  Layers,
  Key,
  MessageSquare,
  Variable,
  Image,
} from 'lucide-react';
import { createLogger } from '@/lib/logger';
import {
  REFERENCE_TYPE_CONFIGS,
  type ExternalReference,
  type ExternalReferenceType,
  type AutoMatchResult,
} from '@/lib/flows/reference-types';
import { ReferenceMappingItem, AUTO_CREATE_VALUE } from './ReferenceMappingItem';
import type { LucideIcon } from 'lucide-react';

/** Logger para o componente */
const log = createLogger('ReferenceMappingContent');

/** Mapeamento de ícones por tipo de referência */
const REFERENCE_ICONS: Partial<Record<ExternalReferenceType, LucideIcon>> = {
  agent: User,
  department: Building2,
  inbox: Inbox,
  contact_tag: Tag,
  deal_tag: Tag,
  workspace: Briefcase,
  pipeline: Layers,
  phase: Layers,
  kanban_category: Layers,
  kanban_phase: Layers,
  distribution_queue: User,
  credential: Key,
  template: MessageSquare,
  seller: User,
  tenant_variable: Variable,
  media_gallery: Image,
};

/** Estatísticas de mapeamento */
interface MappingStats {
  total: number;
  mapped: number;
  required: number;
  requiredMapped: number;
  canComplete: boolean;
}

interface ReferenceMappingContentProps {
  /** Lista completa de referências */
  references: ExternalReference[];
  /** Referências agrupadas por tipo */
  referencesByType: Map<ExternalReferenceType, ExternalReference[]>;
  /** Lista de tipos únicos */
  mappingTypes: ExternalReferenceType[];
  /** Índice do tipo atual sendo editado */
  currentMappingType: number;
  /** Callback para mudar tipo atual */
  onMappingTypeChange: (index: number) => void;
  /** Mapa de mapeamentos (placeholder -> id) */
  mappings: Map<string, string | null>;
  /** Callback para mudança de mapeamento */
  onMappingChange: (placeholder: string, mappedId: string | null) => void;
  /** Resultados de auto-match */
  autoMatches: AutoMatchResult[];
  /** Estatísticas de mapeamento */
  mappingStats: MappingStats;
  /** Progresso de mapeamento (0-100) */
  mappingProgress: number;
  /** Se criação automática de variáveis está ativada */
  autoCreateVariables?: boolean;
  /** Callback para mudança de criação automática */
  onAutoCreateVariablesChange?: (value: boolean) => void;
  /** Callback para criar nova entidade de um tipo (botão "+" no item) */
  onCreateForType?: (type: ExternalReferenceType) => void;
}

/**
 * Componente de conteúdo para mapeamento de referências
 * A navegação entre steps é controlada pelo componente pai (ImportFlowDialog)
 */
export function ReferenceMappingContent({
  references,
  referencesByType,
  mappingTypes,
  currentMappingType,
  onMappingTypeChange,
  mappings,
  onMappingChange,
  autoMatches,
  mappingStats,
  mappingProgress,
  autoCreateVariables = false,
  onAutoCreateVariablesChange,
  onCreateForType,
}: ReferenceMappingContentProps) {

  // Tipo atual
  const currentType = mappingTypes[currentMappingType];

  // Referências do tipo atual
  const currentReferences = referencesByType.get(currentType) || [];

  // Candidatos do tipo atual (extraídos dos auto-matches)
  const currentCandidates = useMemo(() => {
    const typeMatches = autoMatches.filter(m => m.reference.type === currentType);
    const allCandidates = new Map<string, { id: string; name: string; similarity: number }>();
    
    for (const match of typeMatches) {
      for (const candidate of match.candidates) {
        if (!allCandidates.has(candidate.id)) {
          allCandidates.set(candidate.id, candidate);
        }
      }
    }
    
    return Array.from(allCandidates.values());
  }, [autoMatches, currentType]);

  // Verifica se todos mapeamentos foram auto-match
  const allAutoMatched = useMemo(() => {
    if (mappingStats.mapped === 0) return false;
    
    return mappingStats.mapped === references.filter(r => {
      const match = autoMatches.find(m => m.reference.placeholder === r.placeholder);
      return match?.candidates.some(c => c.similarity === 1);
    }).length;
  }, [mappingStats.mapped, references, autoMatches]);

  /** Tipos que suportam criação automática */
  const AUTO_CREATABLE_TYPES: ExternalReferenceType[] = ['tenant_variable', 'contact_tag', 'deal_tag'];

  // Verifica se há tipos auto-criáveis não mapeados (para mostrar toggle)
  const hasUnmappedAutoCreatable = useMemo(() => {
    const autoCreatableRefs = references.filter(r => AUTO_CREATABLE_TYPES.includes(r.type));
    if (autoCreatableRefs.length === 0) return false;
    
    return autoCreatableRefs.some(r => !mappings.has(r.placeholder) || mappings.get(r.placeholder) === null);
  }, [references, mappings]);

  // Verifica se existem tipos auto-criáveis no fluxo
  const hasAutoCreatableTypes = useMemo(() => {
    return references.some(r => AUTO_CREATABLE_TYPES.includes(r.type));
  }, [references]);

  const config = currentType ? REFERENCE_TYPE_CONFIGS[currentType] : null;
  const Icon = currentType ? REFERENCE_ICONS[currentType] || Tag : Tag;

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-4 w-full overflow-hidden relative">
      {/* Header com progresso - NÃO ENCOLHE */}
      <div className="shrink-0 space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Progresso do mapeamento
          </span>
          <span className="font-medium">
            {mappingStats.mapped} de {mappingStats.total}
          </span>
        </div>
        <Progress value={mappingProgress} className="h-2" />
        
        {/* Indicador de obrigatórios */}
        {mappingStats.required > 0 && mappingStats.requiredMapped < mappingStats.required && (
          <div className="flex items-center gap-2 text-xs text-yellow-600">
            <AlertTriangle className="h-3.5 w-3.5" />
            <span>
              {mappingStats.required - mappingStats.requiredMapped} mapeamento{mappingStats.required - mappingStats.requiredMapped !== 1 ? 's' : ''} obrigatório{mappingStats.required - mappingStats.requiredMapped !== 1 ? 's' : ''} pendente{mappingStats.required - mappingStats.requiredMapped !== 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>

      {/* Toggle para criação automática de itens ausentes (variáveis + tags) */}
      {hasAutoCreatableTypes && onAutoCreateVariablesChange && (
        <div className="shrink-0 flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
          <Switch 
            id="auto-create-variables"
            checked={autoCreateVariables}
            onCheckedChange={(checked) => {
              onAutoCreateVariablesChange(checked);
              // Sincronizar toggle com seleções individuais de tipos auto-criáveis
              const autoCreatableRefs = references.filter(r => AUTO_CREATABLE_TYPES.includes(r.type));
              for (const ref of autoCreatableRefs) {
                const currentMapping = mappings.get(ref.placeholder);
                if (checked) {
                  // Toggle ON: itens não mapeados recebem __auto_create__
                  if (!currentMapping || currentMapping === null) {
                    onMappingChange(ref.placeholder, AUTO_CREATE_VALUE);
                  }
                } else {
                  // Toggle OFF: itens com __auto_create__ voltam para null
                  if (currentMapping === AUTO_CREATE_VALUE) {
                    onMappingChange(ref.placeholder, null);
                  }
                }
              }
            }}
          />
          <div className="flex-1">
            <Label htmlFor="auto-create-variables" className="text-sm font-medium cursor-pointer">
              Criar automaticamente itens ausentes
            </Label>
            <p className="text-xs text-muted-foreground">
              Variáveis e tags não encontradas serão criadas no tenant automaticamente
            </p>
          </div>
          <Variable className="h-4 w-4 text-muted-foreground shrink-0" />
        </div>
      )}

      <Separator className="shrink-0" />

      {/* Navegação por tipo - NÃO ENCOLHE, com scroll horizontal e contenção de largura */}
      <div className="shrink-0 relative overflow-hidden" style={{ maxWidth: '100%', width: '100%' }}>
        {/* Gradiente de fade à direita para indicar mais conteúdo */}
        <div className="absolute right-0 top-0 bottom-2 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none z-10" />
        
        <div 
          className="flex items-center gap-2 pb-2 pr-8 overflow-x-auto overflow-y-hidden scrollbar-thin"
          style={{ 
            WebkitOverflowScrolling: 'touch',
            scrollBehavior: 'smooth'
          }}
        >
          {mappingTypes.map((type, index) => {
            const typeConfig = REFERENCE_TYPE_CONFIGS[type];
            const TypeIcon = REFERENCE_ICONS[type] || Tag;
            const refs = referencesByType.get(type) || [];
            const mappedCount = refs.filter(r => mappings.has(r.placeholder)).length;
            const isComplete = mappedCount === refs.length;
            const isCurrent = index === currentMappingType;

            return (
              <Button
                key={type}
                variant={isCurrent ? 'secondary' : 'ghost'}
                size="sm"
                className={`shrink-0 gap-1.5 ${isCurrent ? '' : 'text-muted-foreground'}`}
                onClick={() => onMappingTypeChange(index)}
              >
                <TypeIcon className="h-3.5 w-3.5" />
                <span className="text-xs">{typeConfig?.label || type}</span>
                <Badge 
                  variant={isComplete ? 'secondary' : 'outline'} 
                  className={`ml-1 text-[10px] px-1.5 ${isComplete ? 'bg-green-500/10 text-green-600' : ''}`}
                >
                  {mappedCount}/{refs.length}
                </Badge>
              </Button>
            );
          })}
        </div>
      </div>

      {/* Conteúdo do tipo atual - CRESCE E TEM SCROLL */}
      <div className="flex-1 min-h-0 flex flex-col gap-3 overflow-y-auto">
        {/* Header do tipo - NÃO ENCOLHE */}
        <div className="shrink-0 flex items-center gap-2">
          <Icon className="h-5 w-5 text-primary" />
          <h3 className="font-medium">{config?.label}</h3>
          <Badge variant="secondary">{currentReferences.length}</Badge>
        </div>
        <p className="shrink-0 text-sm text-muted-foreground">
          {config?.description}
        </p>

        {/* Lista de referências para mapear - CRESCE E TEM SCROLL INTERNO */}
        {/* Lista de referências — scroll gerenciado pelo container pai */}
        <div className="space-y-2">
          {currentReferences.map((ref) => {
            const autoMatch = autoMatches.find(m => m.reference.placeholder === ref.placeholder);
            const isRequired = REFERENCE_TYPE_CONFIGS[ref.type]?.required || false;
            const isAutoCreatable = AUTO_CREATABLE_TYPES.includes(ref.type);
            
            return (
              <ReferenceMappingItem
                key={`${ref.placeholder}-${ref.context}`}
                reference={ref}
                referenceType={ref.type}
                autoMatch={autoMatch}
                selectedId={mappings.get(ref.placeholder) || null}
                candidates={currentCandidates}
                onChange={onMappingChange}
                required={isRequired}
                showAutoCreate={isAutoCreatable}
                onCreateNew={
                  onCreateForType && !isAutoCreatable && ref.type !== 'media_gallery'
                    ? () => onCreateForType(ref.type)
                    : undefined
                }
              />
            );
          })}
        </div>
      </div>

      {/* Dica sobre auto-match - NÃO ENCOLHE (condicional) */}
      {allAutoMatched && mappingStats.mapped > 0 && (
        <div className="shrink-0 flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 rounded-lg p-2.5">
          <Sparkles className="h-4 w-4 text-yellow-500 shrink-0" />
          <span>
            Todos os mapeamentos foram sugeridos automaticamente por correspondência de nomes.
          </span>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// EXPORTS LEGACY (para compatibilidade)
// ============================================================================

/**
 * @deprecated Use ReferenceMappingContent diretamente
 * Mantido para compatibilidade com código antigo
 */
export { ReferenceMappingContent as ReferenceMappingWizard };
